import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { getCriticalTimeoutMs } from "utils/request-timeouts";

import { emptySplitApi } from "./empty-split-api";
import type {
  PaywallFeature,
  PaywallFeatureSubscription,
  PaywallFeatureSubscriptionCreateResponse,
  PaywallFeatureSubscriptionFilter,
  PaywallFeatureSubscriptionInput,
  PaywallFeaturesStatus,
  PaywallLicenseFilter
} from "./types/paywall.type";

const DEFAULT_PAYWALL_LICENSE_QUERY =
  "{id,user{id},expires_at,subscription{practice_id}}";

const paywallApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPaywallFeatureByKey: builder.query<PaywallFeature, string>({
      query: (featureKey) => `/paywall/features/?feature_key=${featureKey}`,
      providesTags: ["PaywallFeatures"]
    }),
    getPaywallFeature: builder.query<PaywallFeature, number>({
      query: (id) => `/paywall/features/${id}/`,
      providesTags: ["PaywallFeatures"]
    }),
    getPaywallLicenses: builder.query<
      PaywallFeature[],
      Partial<PaywallLicenseFilter>
    >({
      query: (filter) =>
        `/paywall/features/${formatValidQueryParamUrlCommaSeparated(filter, true)}&query=${DEFAULT_PAYWALL_LICENSE_QUERY}`,
      providesTags: ["PaywallLicenses"]
    }),
    getCurrentUserPaywallFeatures: builder.query<PaywallFeaturesStatus, void>({
      // Critical bootstrap sibling — armed with the LD bootstrap timeout
      // (undefined = off = no timeout). TIMEOUT_ERROR retries via the wrapper.
      query: () => ({
        url: `auth/users/me/features/`,
        timeout: getCriticalTimeoutMs()
      }),
      providesTags: ["PaywallUserFeatures"]
    }),
    getFeatureSubscriptions: builder.query<
      PaywallFeatureSubscription[],
      Partial<PaywallFeatureSubscriptionFilter>
    >({
      query: (filter) =>
        `/paywall/feature-subscriptions/${formatValidQueryParamUrlCommaSeparated(filter, true)}`,
      providesTags: ["PaywallFeatureSubscriptions"]
    }),
    createFeatureSubscription: builder.mutation<
      PaywallFeatureSubscriptionCreateResponse,
      PaywallFeatureSubscriptionInput
    >({
      query: (body) => ({
        url: `/paywall/feature-subscriptions/`,
        method: "POST",
        body
      }),
      invalidatesTags: [
        "PaywallUserFeatures",
        "PaywallFeatureSubscriptions",
        "PaywallLicenses"
      ]
    })
  })
});

export const {
  useGetPaywallFeatureByKeyQuery,
  useGetCurrentUserPaywallFeaturesQuery,
  useLazyGetCurrentUserPaywallFeaturesQuery,
  useCreateFeatureSubscriptionMutation,
  useGetFeatureSubscriptionsQuery
} = paywallApi;
